/**
 * ACPF dispatch — the AC counterpart of `multiTimestepOptimization.ts`.
 *
 * Adapts the app's `GridState` (topology + the optional AC overrides on each
 * node/edge) into an AC network, grounds the AC power-flow rules into a residual
 * system, solves F(x) = 0 by Newton-Raphson, and maps the result back onto the
 * grid (per-bus voltage magnitude and angle, per-branch flow) plus a
 * {@link GroundedProblem} for the elements panel.
 *
 * Unlike the DCOPF path this is a single snapshot: AC power flow has no objective
 * and no inter-hour coupling, so there's one system to solve, not one big LP over
 * all hours. Injections are *specified* (a generator's setpoint, a load's demand)
 * rather than optimized — that's the ACFields the topology carries.
 *
 * The topology → AC mapping is best-effort: it honors any `ac` overrides and
 * otherwise derives defaults from the existing DC fields (edge reactance/
 * resistance, generator capacity, load demand, one designated slack bus), so
 * ACPF runs on any DC network. A proper AC case sets the `ac` fields explicitly.
 */

import { GridState, GridNode, GridEdge, BusKind } from '../types';
import { GridStateWithSolution } from './multiTimestepOptimization';
import { logger } from '../utils/logger';
import { Data, RuleProgram, groundData } from './language';
import { buildAcPfData, flatStartFor, Bus, Branch } from './nonlinear/acPowerFlow';
import { groundResiduals } from './nonlinear/residual';
import { newton, NewtonResult } from './nonlinear/newton';
import { GroundedProblem, acGroundedProblem, GroundedResidualSystem } from './groundedProblem';

/** Per-unit base used to convert MW/MVAr topology values to per unit. */
export const BASE_MVA = 100;

/** The single AC snapshot: the dispatched grid state plus the grounded problem. */
export interface AcDispatchResult {
  gridState: GridStateWithSolution;
  problem: GroundedProblem;
  /** The AC input relations (buses, branches, admittances, injections) — the DATA tables. */
  data: Data;
  /** The grounded residual system — the residual DAG panel renders it. */
  system: GroundedResidualSystem;
}

/** Pick the slack bus: an explicitly-marked one, else the first bus node, else node 0. */
function slackNodeId(nodes: GridNode[]): string | undefined {
  const explicit = nodes.find((n) => n.ac?.busKind === 'slack');
  if (explicit) return explicit.id;
  const bus = nodes.find((n) => n.type === 'bus');
  return (bus ?? nodes[0])?.id;
}

function busKindFor(node: GridNode, isSlack: boolean): BusKind {
  if (node.ac?.busKind) return node.ac.busKind;
  if (isSlack) return 'slack';
  // A generator holding a voltage setpoint is a PV bus; everything else is PQ.
  if (node.type === 'generator' && node.ac?.vSet !== undefined) return 'pv';
  return 'pq';
}

/** Net real injection (pu): explicit setpoint, else +gen capacity / −load demand. */
function pInjFor(node: GridNode): number {
  if (node.ac?.pSet !== undefined) return node.ac.pSet;
  const mw = Math.abs(node.maxPowerMW ?? node.value ?? 0) / BASE_MVA;
  if (node.type === 'generator') return mw;
  if (node.type === 'load') return -mw;
  return 0;
}

function nodeToBus(node: GridNode, isSlack: boolean): Bus {
  return {
    id: node.id,
    kind: busKindFor(node, isSlack),
    vSet: node.ac?.vSet ?? 1.0,
    pInj: pInjFor(node),
    qInj: node.ac?.qSet ?? 0,
    shuntB: node.ac?.shuntB ?? 0,
  };
}

function edgeToBranch(edge: GridEdge): Branch {
  // Fall back to the DC reactance/resistance; a zero/absent reactance would make
  // the series admittance singular, so give it a small nonzero default.
  const x = edge.ac?.x ?? (edge.reactance && edge.reactance !== 0 ? edge.reactance : 0.1);
  return {
    id: edge.id,
    from: edge.from,
    to: edge.to,
    r: edge.ac?.r ?? edge.resistance ?? 0,
    x,
    b: edge.ac?.b ?? 0,
    tap: edge.ac?.tap ?? 1,
  };
}

/** Map the grid topology to an AC network (buses + branches). */
export function topologyToAcNetwork(grid: GridState): { buses: Bus[]; branches: Branch[] } {
  const slackId = slackNodeId(grid.nodes);
  return {
    buses: grid.nodes.map((node) => nodeToBus(node, node.id === slackId)),
    branches: grid.edges.map(edgeToBranch),
  };
}

/**
 * Solve one AC power-flow snapshot for a grid. Never throws: a non-square or
 * singular system (e.g. a disconnected bus) is reported as a non-converged
 * result, so the panel shows the problem and its status rather than crashing.
 */
export function solveAcPowerFlow(grid: GridState, program: RuleProgram): AcDispatchResult {
  const { buses, branches } = topologyToAcNetwork(grid);
  // The base input relations, plus anything the (editable) definition rules
  // compute from them — the same grounding path DCOPF uses.
  const data = groundData(program, buildAcPfData(buses, branches));
  const system: GroundedResidualSystem = groundResiduals(
    program.constraintRules,
    program.catalog,
    data,
  );
  const seed = flatStartFor(buses, system.variables);

  let result: NewtonResult;
  try {
    result = newton(system.residuals, system.variables, seed);
  } catch (error) {
    logger.warn('AC power flow did not solve:', error);
    // Report the system at its starting point, marked not converged.
    result = {
      converged: false,
      iterations: 0,
      x: seed,
      residualNorm: Infinity,
      history: [],
    };
  }

  const problem = acGroundedProblem(system, result);

  const nodes: GridNode[] = grid.nodes.map((node) => ({
    ...node,
    value: pInjFor(node) * BASE_MVA,
    voltage: result.x.get(`V_${node.id}_t0`),
    voltageAngle: result.x.get(`theta_${node.id}_t0`),
  }));
  const edges: GridEdge[] = grid.edges.map((edge) => ({
    ...edge,
    // Real power leaving the from-end into the branch (pu).
    current: result.x.get(`P_from_${edge.id}_t0`),
  }));

  return {
    gridState: {
      nodes,
      edges,
      solutionValid: result.converged,
      solutionMessage: problem.status.message,
    },
    problem,
    data,
    system,
  };
}
